import { useState, type FormEvent } from 'react';
import { setDeviceToken } from '../../auth/deviceToken';

// Shown when the kiosk has no device token. The household member generates a
// code from Budget → Settings → Kiosks → Add, types it here, and the kiosk
// trades the code for a long-lived device token via /api/kiosk/enroll/complete.

interface EnrollResponse {
  token?: string;
  device?: { id: number; label: string };
  error?: string;
}

export function KioskEnroll() {
  const [code, setCode] = useState('');
  const [label, setLabel] = useState('Kitchen');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      const res = await fetch('/api/kiosk/enroll/complete', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code: code.trim().toUpperCase(), label: label.trim() }),
      });
      const json = (await res.json().catch(() => ({}))) as EnrollResponse;
      if (!res.ok || typeof json.token !== 'string') {
        setError(json.error ?? 'Enrollment failed. Try a new code.');
        setSubmitting(false);
        return;
      }
      setDeviceToken(json.token);
      window.location.reload();
    } catch {
      setError('Network error. Try again.');
      setSubmitting(false);
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-canvas text-fg">
      <div className="card w-full max-w-sm flex flex-col gap-5">
        <header className="flex flex-col gap-1">
          <h1 className="text-xl font-semibold">Link this kiosk</h1>
          <p className="text-sm text-fg-secondary">
            On your phone, open Budget → Settings → Kiosks → Add Kiosk to generate
            a code, then type it here.
          </p>
        </header>
        <form onSubmit={onSubmit} className="flex flex-col gap-4">
          <label className="label flex flex-col gap-1.5">
            <span>Label</span>
            <input
              type="text"
              className="input"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              maxLength={64}
              required
              disabled={submitting}
            />
          </label>
          <label className="label flex flex-col gap-1.5">
            <span>Code</span>
            <input
              type="text"
              className="input tabular tracking-widest uppercase"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              autoCapitalize="characters"
              autoComplete="off"
              spellCheck={false}
              maxLength={20}
              required
              disabled={submitting}
            />
          </label>
          {error && <p className="auth-error" role="alert">{error}</p>}
          <button type="submit" className="btn btn-primary" disabled={submitting}>
            {submitting ? 'Linking…' : 'Link kiosk'}
          </button>
        </form>
      </div>
    </div>
  );
}
